import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Paper,
  Stack,
  Chip,
  Button,
} from "@mui/material";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import SettingsInputComponentRoundedIcon from "@mui/icons-material/SettingsInputComponentRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function CompressionLearning() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";
  const pageUrl = "https://parishaudio.com/learning/compression";
  const pageTitle = "What Is Audio Compression in Music Production?";
  const pageDescription =
    "Learn what audio compression is, how compressors work, what the main controls do, common compression techniques, compressor types, and practical ways to use compression in music production.";

  const schema = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        "@id": `${pageUrl}#article`,
        headline: pageTitle,
        description: pageDescription,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `${pageUrl}#webpage`,
        },
        url: pageUrl,
        inLanguage: "en-US",
        articleSection: "Audio Education",
        keywords: [
          "audio compression",
          "dynamic range compression",
          "compressor settings",
          "music production compression",
          "mixing compression",
          "compressor controls",
          "sidechain compression",
          "parallel compression",
          "multiband compression",
        ],
        author: {
          "@type": "Organization",
          name: "Parish Audio",
          url: "https://parishaudio.com",
        },
        publisher: {
          "@type": "Organization",
          name: "Parish Audio",
          url: "https://parishaudio.com",
          logo: {
            "@type": "ImageObject",
            url: "https://parishaudio.com/assets/logo.png",
          },
        },
        image: {
          "@type": "ImageObject",
          url: "https://parishaudio.com/RecordingStudioStockImage1.jpg",
        },
        about: [
          { "@type": "Thing", name: "Audio compression" },
          { "@type": "Thing", name: "Dynamic range" },
          { "@type": "Thing", name: "Mixing" },
          { "@type": "Thing", name: "Mastering" },
          { "@type": "Thing", name: "Music production" },
        ],
        isPartOf: {
          "@type": "CollectionPage",
          name: "Parish Audio Learning Center",
          url: "https://parishaudio.com/learning",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumbs`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://parishaudio.com",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Learning Center",
            item: "https://parishaudio.com/learning",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "Compression",
            item: pageUrl,
          },
        ],
      },
    ],
    []
  );

  const quickFacts = [
    {
      title: "What it does",
      text: "Compression reduces dynamic range so louder and quieter parts sit closer together.",
      icon: <VolumeUpRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Why it matters",
      text: "It helps control peaks, add consistency, increase sustain, and shape musical feel.",
      icon: <AutoAwesomeRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Where it is used",
      text: "Vocals, drums, bass, buses, mastering, broadcasting, live sound, and speech.",
      icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const parameters = [
    {
      name: "Threshold",
      description:
        "Sets the level where compression begins. Lower thresholds cause more of the signal to be compressed.",
    },
    {
      name: "Ratio",
      description:
        "Controls how strongly the signal is reduced once it crosses the threshold. Higher ratios mean heavier compression.",
    },
    {
      name: "Attack",
      description:
        "Determines how quickly the compressor reacts. Fast attack catches sharp transients, while slower attack lets more initial punch through.",
    },
    {
      name: "Release",
      description:
        "Controls how quickly the compressor stops reducing gain after the signal falls back below threshold.",
    },
    {
      name: "Knee",
      description:
        "A hard knee applies compression more abruptly, while a soft knee makes the transition gentler and smoother.",
    },
    {
      name: "Make-Up Gain",
      description:
        "Raises the level after compression so the signal can regain loudness without restoring the original peaks.",
    },
    {
      name: "Peak vs RMS Detection",
      description:
        "Peak detection reacts to fast spikes, while RMS-style behavior follows average level more closely and often sounds more natural.",
    },
  ];

  const techniques = [
    {
      name: "Sidechain Compression",
      description:
        "Uses an external signal to trigger gain reduction. Common for ducking music under speech or making bass pull back when the kick hits.",
    },
    {
      name: "Parallel Compression",
      description:
        "Blends a heavily compressed signal with the original dry signal to keep energy and impact while preserving some natural dynamics.",
    },
    {
      name: "Multiband Compression",
      description:
        "Splits the audio into separate frequency bands so each range can be compressed independently.",
    },
    {
      name: "Serial Compression",
      description:
        "Uses multiple compressors in sequence, each doing a smaller amount of work for a smoother, more controlled result.",
    },
  ];

  const compressorTypes = [
    {
      name: "Optical",
      description:
        "Often smooth and natural sounding, commonly used on vocals and other sources that benefit from gentle musical control.",
    },
    {
      name: "FET",
      description:
        "Fast and punchy, often chosen for drums, vocals, and sources where attack and energy matter.",
    },
    {
      name: "VCA",
      description:
        "Precise and versatile, often used for bus compression, mix control, and mastering-style work.",
    },
    {
      name: "Tube / Variable-Mu",
      description:
        "Known for warm, rounded, musical compression that can work beautifully on vocals and mix bus material.",
    },
  ];

  const bestPractices = [
    "Use only as much compression as the material actually needs.",
    "Set attack and release by ear instead of relying only on preset numbers.",
    "Match the compressor behavior to the source: vocals, drums, bass, and buses all respond differently.",
    "Use bypass frequently to confirm the compressor is improving the sound instead of just making it louder.",
    "Be cautious with excessive compression, especially on full mixes, since it can reduce life and increase listener fatigue.",
  ];

  const outerPanelSx = {
    width: "100%",
    minWidth: 0,
    borderRadius: "20px",
    overflow: "hidden",
    backgroundColor: style.color1,
    border: `1px solid ${style.color2}`,
    boxSizing: "border-box",
  };

  const subtlePanelSx = {
    borderRadius: "14px",
    backgroundColor: style.darkerPanel,
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "none",
  };

  return (
    <>
      <Seo
        title="What Is Audio Compression? | Parish Audio Learning"
        description={pageDescription}
        canonical={pageUrl}
        schema={schema}
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2.5 },
          boxSizing: "border-box",
          minHeight: height - 130,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", minWidth: 0 }}>
          <Stack spacing={{ xs: 2, md: 2.5 }}>
            <Paper
              elevation={0}
              sx={{
                ...outerPanelSx,
                minHeight: { xs: 260, md: 310 },
                display: "flex",
                alignItems: "center",
                backgroundImage:
                  'linear-gradient(90deg, rgba(8,12,18,0.84) 0%, rgba(8,12,18,0.74) 38%, rgba(8,12,18,0.42) 68%, rgba(8,12,18,0.22) 100%), url("/RecordingStudioStockImage1.jpg")',
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
                <Box sx={{ maxWidth: 760 }}>
                  <Stack spacing={1.25} alignItems="flex-start">
                    <Chip
                      label="Learning Center"
                      sx={{
                        borderRadius: "5px",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    />

                    <Typography
                      variant="h1"
                      sx={{
                        fontSize: { xs: "2.2rem", md: "3.35rem" },
                        fontWeight: 800,
                        lineHeight: 1.02,
                        color: style.color4,
                        letterSpacing: "-0.04em",
                      }}
                    >
                      Audio Compression
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 700,
                      }}
                    >
                      Compression is one of the most important tools in audio production.
                      It can make performances feel more controlled, increase sustain,
                      improve consistency, and help sounds sit properly in a mix. Used
                      well, compression adds polish and focus. Used carelessly, it can
                      flatten life out of a track. Understanding how compressors work is
                      a major step toward making better mixing decisions.
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              {quickFacts.map((item) => (
                <Paper key={item.title} elevation={0} sx={{ ...outerPanelSx }}>
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      {item.icon}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: style.color4,
                            mb: 0.5,
                            lineHeight: 1.2,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: style.color3,
                            lineHeight: 1.55,
                          }}
                        >
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Paper>
              ))}
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <GraphicEqRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Understanding Dynamic Range Compression
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Dynamic range is the difference between the quietest and loudest
                    parts of a signal. A compressor reduces that range by turning down
                    louder sections, and in some workflows by helping quieter details
                    come forward relative to the peaks.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    In practice, compression is often used to balance uneven levels,
                    control transients, increase sustain, prevent overload, and help
                    tracks fit together more smoothly in a mix. The goal is not simply
                    to make something louder. The goal is to shape how it behaves over time.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Compression is commonly used on vocals, drums, bass, guitars,
                    synths, buses, podcasts, broadcasts, and full mixes. Used
                    thoughtfully, it can make a sound feel more polished, solid,
                    and emotionally controlled.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Main Types of Compression
                      </Typography>
                    </Stack>

                    <Box
                      component="ul"
                      sx={{
                        pl: 2.5,
                        m: 0,
                        color: style.color3,
                        "& li": { mb: 1.2, lineHeight: 1.7 },
                      }}
                    >
                      <li>
                        <strong style={{ color: style.color4 }}>Downward compression:</strong>{" "}
                        reduces the level of signals above a threshold. This is the most
                        common type and what most people mean when they say compression.
                      </li>
                      <li>
                        <strong style={{ color: style.color4 }}>Upward compression:</strong>{" "}
                        raises quieter material relative to louder content, helping subtle
                        details come forward.
                      </li>
                      <li>
                        <strong style={{ color: style.color4 }}>Feed-forward designs:</strong>{" "}
                        analyze the input before gain reduction is applied, often producing
                        a more controlled and precise response.
                      </li>
                      <li>
                        <strong style={{ color: style.color4 }}>Feedback designs:</strong>{" "}
                        react partly to the processed output, which can produce a smoother,
                        more musical feel.
                      </li>
                    </Box>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <SettingsInputComponentRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Why Compression Changes Feel
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                      Compressors do more than change level. They influence punch,
                      sustain, density, smoothness, aggression, and perceived closeness.
                    </Typography>

                    <Box
                      component="ul"
                      sx={{
                        pl: 2.5,
                        m: 0,
                        color: style.color3,
                        "& li": { mb: 1.2, lineHeight: 1.7 },
                      }}
                    >
                      <li>
                        <strong style={{ color: style.color4 }}>Fast attack:</strong>{" "}
                        can soften transients and make a sound feel tighter or flatter.
                      </li>
                      <li>
                        <strong style={{ color: style.color4 }}>Slower attack:</strong>{" "}
                        can preserve punch and let more initial impact through.
                      </li>
                      <li>
                        <strong style={{ color: style.color4 }}>Short release:</strong>{" "}
                        can sound energetic or obvious depending on the material.
                      </li>
                      <li>
                        <strong style={{ color: style.color4 }}>Long release:</strong>{" "}
                        can sound smoother, but may reduce movement if overdone.
                      </li>
                    </Box>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TuneRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Compressor Parameters and Controls
                    </Typography>
                  </Stack>

                  <Stack spacing={1.2}>
                    {parameters.map((param) => (
                      <Paper
                        key={param.name}
                        elevation={0}
                        sx={{
                          ...subtlePanelSx,
                          p: 1.5,
                        }}
                      >
                        <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.4 }}>
                          {param.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: style.color3, lineHeight: 1.65 }}
                        >
                          {param.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Special Compression Techniques
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {techniques.map((technique) => (
                      <Paper
                        key={technique.name}
                        elevation={0}
                        sx={{
                          ...subtlePanelSx,
                          p: 1.5,
                        }}
                      >
                        <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.4 }}>
                          {technique.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: style.color3, lineHeight: 1.65 }}
                        >
                          {technique.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LibraryMusicRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Applications of Compression
                      </Typography>
                    </Stack>

                    <Box
                      component="ul"
                      sx={{
                        pl: 2.5,
                        m: 0,
                        color: style.color3,
                        "& li": { mb: 1.2, lineHeight: 1.7 },
                      }}
                    >
                      <li>
                        <strong style={{ color: style.color4 }}>Music production:</strong>{" "}
                        smooth vocals, tighten bass, shape drum punch, and add cohesion to buses.
                      </li>
                      <li>
                        <strong style={{ color: style.color4 }}>Mastering:</strong>{" "}
                        control peaks and shape perceived loudness while preserving balance.
                      </li>
                      <li>
                        <strong style={{ color: style.color4 }}>Broadcasting:</strong>{" "}
                        keep program levels more consistent and easier to hear.
                      </li>
                      <li>
                        <strong style={{ color: style.color4 }}>Live sound:</strong>{" "}
                        reduce sudden spikes, improve clarity, and help protect systems.
                      </li>
                      <li>
                        <strong style={{ color: style.color4 }}>Speech workflows:</strong>{" "}
                        improve intelligibility and keep dialogue more controlled.
                      </li>
                    </Box>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TuneRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Common Compressor Types
                      </Typography>
                    </Stack>

                    <Stack spacing={1.2}>
                      {compressorTypes.map((type) => (
                        <Paper
                          key={type.name}
                          elevation={0}
                          sx={{
                            ...subtlePanelSx,
                            p: 1.5,
                          }}
                        >
                          <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.4 }}>
                            {type.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: style.color3, lineHeight: 1.65 }}
                          >
                            {type.description}
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Overuse and the Loudness Problem
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Compression is powerful, but too much of it can make audio feel flat,
                    lifeless, or fatiguing. Excessive gain reduction and limiting can erase
                    contrast between soft and loud moments, which is one reason the loudness
                    war became such a big issue in modern mastering.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    More loudness is not always better. A great mix often feels strong not
                    just because it is loud, but because it still has shape, movement,
                    punch, and musical breathing room.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Best Practices
                      </Typography>
                    </Stack>

                    <Box
                      component="ul"
                      sx={{
                        pl: 2.5,
                        m: 0,
                        color: style.color3,
                        "& li": { mb: 1.25, lineHeight: 1.75 },
                      }}
                    >
                      {bestPractices.map((tip, index) => (
                        <li key={index}>{tip}</li>
                      ))}
                    </Box>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Final Thoughts
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      Compression is one of the most useful and most misunderstood tools
                      in audio. It can solve problems, shape feel, improve consistency,
                      and add musical energy, but only when it is used intentionally.
                    </Typography>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      The best way to learn compression is to listen closely. Adjust one
                      control at a time, compare with bypass often, and pay attention to
                      how the sound changes emotionally, not just technically.
                    </Typography>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      When used thoughtfully, compression can help a mix feel polished,
                      clear, and powerful without sacrificing musicality.
                    </Typography>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Typography sx={{ color: style.color3, lineHeight: 1.7 }}>
                    Related topics:
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                      component={RouterLink}
                      to="/learning/equalization"
                      sx={{
                        borderRadius: "5px",
                        textTransform: "none",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.05)",
                        border: `1px solid ${accent}`,
                        "&:hover": {
                          borderColor: style.color4,
                          backgroundColor: "rgba(255,255,255,0.10)",
                        },
                      }}
                    >
                      Equalization
                    </Button>
                    <Button
                      component={RouterLink}
                      to="/learning/delay"
                      sx={{
                        borderRadius: "5px",
                        textTransform: "none",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.05)",
                        border: `1px solid ${accent}`,
                        "&:hover": {
                          borderColor: style.color4,
                          backgroundColor: "rgba(255,255,255,0.10)",
                        },
                      }}
                    >
                      Delay
                    </Button>
                    <Button
                      component={RouterLink}
                      to="/learning/limiting"
                      sx={{
                        borderRadius: "5px",
                        textTransform: "none",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.05)",
                        border: `1px solid ${accent}`,
                        "&:hover": {
                          borderColor: style.color4,
                          backgroundColor: "rgba(255,255,255,0.10)",
                        },
                      }}
                    >
                      Limiting
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
                <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.7 }}>
                  * Parish Audio is not affiliated with any brands, products, or artists
                  mentioned for educational context on this page. *
                </Typography>
              </Box>
            </Paper>
          </Stack>
        </Box>
      </Box>
    </>
  );
}


// import {React, useState, useContext, useEffect, useRef} from "react"
// import {useStyle} from '../../context/styleContext/styleContext'
// import ReactGA from 'react-ga4';
// import { Paper } from '@mui/material';
// import { Container, Typography, Divider, Box, List, ListItem, ListItemText } from "@mui/material";
// import WikiCommonsImage from "../../components/WikiCommonsImage/WikiCommonsImage";


// export default function CompressionLearning(){

//   const { style, setStyle } = useStyle();
//   const [width, setWidth] = useState(window.innerWidth)

// function handleWindowSizeChange() {
//     setWidth(window.innerWidth);
// }
// useEffect(() => {
//     window.addEventListener('resize', handleWindowSizeChange);
//     return () => {
//         window.removeEventListener('resize', handleWindowSizeChange);
//     }
// }, []);

// const isMobile = width <= 768;

// useEffect(() => {
//   document.title = 'Parish Audio | Cutting-Edge Audio Processing | Compression';
// }, []);

// ReactGA.send({
//     hitType:"pageview",
//     page:"learning/compression",
//     title:"learning/compression",
//   })

//   const [height, setHeight] = useState(window.innerHeight)

//   useEffect(() => {
//     function handleResize() {      
//       setWidth(window.innerWidth)
//       setHeight(window.innerHeight)
//   }
  
//     window.addEventListener('resize', handleResize)
//   })

// const topContainer = useRef();
//     useEffect(() => {
//         // To make sure page starts from the top
//         topContainer.current.scrollIntoView({ block: "end" });
//     }, []);




//     return(
//         <><div style={{margin:'auto', width:'95%', maxWidth:'800px'}}>

//         <h1 style={{marginTop:'25px'}} ref={topContainer} >Audio Compression</h1>
//         <Divider className="mobile-card-divider" />
//         <h2 style={{marginTop:'25px'}}>Introduction</h2>
// <p>Audio compression, specifically dynamic range compression (DRC), is a crucial tool in audio engineering, used to manipulate the volume levels of an audio signal. By reducing the difference between the loudest and softest parts of a sound, compression helps maintain a more consistent volume, enhances clarity, and adds character to a mix. It is widely used in music production, broadcasting, live sound, and various other applications. This document explores the principles, types, controls, uses, and advanced applications of audio compression.</p>

// <h2 style={{marginTop:'30px'}}>Understanding Dynamic Range Compression</h2>
// <p>Dynamic range refers to the difference between the quietest and loudest parts of an audio signal. Compression reduces this range by attenuating loud sounds or amplifying soft sounds. The device or software that performs this function is called a compressor.</p>
// <p>In practice, compression is used to:</p>
// <ul class="a">
//     <li style={{marginBottom:'5px'}}>Balance levels in recordings</li>
//     <li style={{marginBottom:'5px'}}>Balance levels in recordings</li>
//     <li style={{marginBottom:'5px'}}>Control transients (sudden spikes in volume)</li>
//     <li style={{marginBottom:'5px'}}>Prevent distortion and clipping in a mix</li>
//     <li style={{marginBottom:'5px'}}>Create special audio effects</li>
// </ul>
// <p>
// Compression is commonly applied to vocals, drums, bass, guitars, and entire mixes to achieve a polished, professional sound.
// </p>


// <h2 style={{marginTop:'30px'}}>Types of Compression</h2>
// <p>There are two main types of dynamic range compression:</p>
// <ul class="a">
//     <li><strong>Downward Compression:</strong> Reduces the volume of sounds above a certain threshold while leaving quieter sounds unchanged. This is the most common type of compression.</li>
//     <li><strong>Upward Compression:</strong> Increases the volume of quiet sounds while leaving louder sounds unaffected. This helps make softer details more audible.</li>
// </ul>

// <h2 style={{marginTop:'30px'}}>Compressor Design and Operation</h2>
// <p>A compressor works by analyzing an audio signal and applying gain reduction based on specific parameters. The two main designs are:</p>
// <ul class="a">
//     <li><strong>Feed-Forward Compression: </strong>The input signal is analyzed before gain reduction is applied.</li>
//     <li><strong>Feedback Compression:</strong>The output signal is analyzed, affecting gain changes retroactively.</li>
// </ul>
// <p>Compression can be implemented using analog hardware (e.g., tube, optical, FET, VCA compressors) or digital software plugins in digital audio workstations (DAWs).</p>

// <h2 style={{marginTop:'30px'}}>Key Compressor Parameters</h2>
// <p>Compression effectiveness depends on adjusting various control parameters:</p>
// <ul class="a">
//     <li><strong>Threshold:</strong> Sets the level above (or below) which compression starts. A lower threshold results in more compression.</li>
//     <li><strong>Ratio:</strong> Determines the degree of compression. A 4:1 ratio means that for every 4 dB above the threshold, the output only increases by 1 dB. Higher ratios (10:1 or more) are considered limiting, which prevents signals from exceeding a certain level.</li>
//     <li><strong>Attack Time:</strong> Defines how quickly compression engages when the signal exceeds the threshold. Faster attack times control transients, while slower attack times preserve natural dynamics.</li>
//     <li><strong>Release Time:</strong> Controls how quickly the compression stops once the signal falls below the threshold. Shorter release times can make compression more noticeable, while longer times ensure smoothness.</li>
//     <li><strong>Knee:</strong> Determines how smoothly compression is applied. A hard knee engages suddenly, while a soft knee gradually increases gain reduction.</li>
//     <li><strong>Make-Up Gain:</strong> Restores lost volume due to compression, making the overall level louder without increasing peak levels.</li>
//     <li><strong>Peak vs. RMS Sensing:</strong>Peak compression reacts to instantaneous peaks, while RMS compression measures the average level, mimicking human loudness perception.</li>
// </ul>
// <h2 style={{marginTop:'30px'}}>Special Compression Techniques</h2>
// <h3>Sidechain Compression</h3>
// <p>Sidechain compression uses an external audio source to control gain reduction. A common example is ducking, where background music lowers when a voice-over is detected (e.g., in radio broadcasts). In electronic music, sidechaining is often applied to basslines triggered by the kick drum to prevent clashing and create a pulsating effect.</p>
// <h3>Parallel Compression (New York Compression)</h3>
// <p>Parallel compression blends a heavily compressed signal with the original, preserving natural dynamics while increasing perceived loudness. This technique is widely used in drum processing and vocals.</p>
// <h3>Multiband Compression</h3>
// <p>Instead of applying compression to the entire frequency range, multiband compression splits the signal into different frequency bands, allowing for independent compression settings. This is useful in mastering and broadcasting to maintain balance across the audio spectrum.</p>
// <h3>Serial Compression</h3>
// <p>This method involves chaining multiple compressors in sequence, each applying a moderate amount of compression. This allows for smoother and more transparent compression compared to a single aggressive compressor.</p>
// <h2>Applications of Compression</h2>
// <h3>Music Production</h3>
// <p>Compression is vital in mixing and mastering. It helps ensure that instruments and vocals sit well in the mix and controls dynamic inconsistencies. Specific applications include:</p>

// <ul class="a">
//     <li><strong>Vocals:</strong>  Controls volume fluctuations and enhances clarity.</li>
//     <li><strong>Drums:</strong> Adds punch and sustain.</li>
//     <li><strong>Guitars/Bass:</strong> Maintains consistent volume and sustain.</li>
//     <li><strong>Mastering:</strong>  Increases loudness while preserving dynamics.</li>
// </ul>

// <h3>Broadcasting and Public Spaces</h3>
// <p>Broadcasters use compression to ensure consistent audio levels and to comply with peak volume regulations. Loudness normalization (e.g., EBU R128, ITU-R BS.1770) has been implemented to prevent drastic volume differences between programs and advertisements.</p>

// <h3>Hearing Aids and Speech Processing</h3>
// <p>Hearing aids use compression to amplify soft sounds while preventing loud noises from becoming overwhelming. Speech processors in mobile phones and communication devices apply compression to improve intelligibility.</p>
// <h3>Live Sound and PA Systems</h3>
// <p>Compression is used in live sound to prevent sudden level spikes, improve clarity, and protect speakers from damage. It ensures speech remains audible in noisy environments.</p>
// <h2>Overuse and the Loudness War</h2>
// <p>While compression is a powerful tool, excessive use can lead to over-compressed audio, reducing dynamic range and making music sound lifeless. This has led to the loudness war, where mastering engineers push loudness at the cost of clarity. The use of brick wall limiting and aggressive compression can introduce distortion and listener fatigue.</p>
// <div style={{marginLeft:'5%', marginRight:'5%', width:'95%', maxWidth:'400px', textAlign:'left'}}>
//         <WikiCommonsImage width="100%" bottomText='"Something" by The Beatles - Mastered 4 times ' bottomTextLinkUrl="https://www.thebeatles.com/" wikiCommonsLink="https://commons.wikimedia.org/wiki/File:Cd_loudness_trend-something.gif" imageSource="https://upload.wikimedia.org/wikipedia/commons/a/ab/Cd_loudness_trend-something.gif"  title = "en:User:Aquegg, Public domain, via Wikimedia Commons" />
// </div>
// <h2 style={{marginTop:'50px'}}>Advanced Compressor Types and Usage</h2>
// <h3>Optical Compressors</h3>
// <p>These use a light-dependent resistor to control gain, offering smooth and natural compression. Common in vocal processing.</p>
// <h3>FET Compressors</h3>
// <p>Field-effect transistor (FET) compressors provide fast attack times and are often used for adding punch to drums and vocals.</p>
// <h3>VCA Compressors</h3>
// <p>Voltage-controlled amplifiers (VCA) allow precise control and are commonly used for bus compression in mixing and mastering.</p>
// <h3>Tube Compressors (Variable-Mu)</h3>
// <p>These use vacuum tubes to apply gain reduction, producing a warm, musical compression ideal for vocals and master bus processing.</p>
// <h2>Best Practices for Using Compression</h2>
// <ul class="a">
//     <li><strong>Use Compression Sparingly:</strong>Avoid over-compressing; dynamic variation is crucial for natural sound.</li>
//     <li><strong>Adjust Attack and Release Properly:</strong> Fast attack times can kill transients, while slow attack times can make the compression ineffective.</li>
//     <li><strong>Match Compression to the Material:</strong> Vocals require different settings than drums or bass.</li>
//     <li><strong>Combine Multiple Compression Techniques:</strong> Use a mix of parallel, multiband, and serial compression where needed.</li>
//     <li><strong>Use Sidechaining for Clarity:</strong> Especially useful for balancing bass and kick drum.</li>
//     <li><strong>Always Compare with Bypass:</strong> Regularly A/B test to ensure compression enhances rather than degrades the sound.</li>
// </ul>
// <p>
// Audio compression is an essential technique for achieving a polished, professional sound in music production, broadcasting, live sound, and speech processing. By understanding its principles, types, and applications, audio engineers and producers can harness compression effectively while avoiding common pitfalls. When used properly, compression can enhance clarity, balance dynamics, and create engaging mixes that stand out without sacrificing musicality.
// </p>
// <br />
//     <br />
//     <br />
//         <p style={{textAlign:'center'}}>* We are not affiliated with any of the brands or artists listed on this site *</p>
      
//       </div>
//         </>

//     )
// }

